import { useState, useRef } from "react";
import { useSelector } from "react-redux";

import apiAttributes from "../../../../../api/attributes";
import apiProducts from "../../../../../api/products";

import { Group } from "../types/Group";

import displayNotification from "../../../../../utilities/displayNotification";

interface Attribute {
  id: number;
  name: string;
  type?: string;
}

const returnUpdatedAttributesList = (
  prevAttributes: Attribute[],
  editedAttribute: Attribute
) => {
  const updatedAttributesList = prevAttributes.map((attribute: any) => {
    if (attribute.id === editedAttribute.id) {
      return {
        ...editedAttribute,
      };
    }

    return attribute;
  });

  return updatedAttributesList;
};

const useAttributes: (data: { initialLoading?: boolean }) => any = ({
  initialLoading,
}) => {
  const initalState = initialLoading ? "LOADING" : [];

  const [availableAttributes, setAvailableAttributes] =
    useState<any>(initalState);
  const availableAttributesRef = useRef();
  availableAttributesRef.current = availableAttributes;

  const [attributes, setAttributes] = useState<any>(initalState);
  const attributesRef = useRef();
  attributesRef.current = attributes;

  const [attributesList, setAttributesList] = useState<any>(initalState);
  const attributesListRef = useRef();
  attributesListRef.current = attributesList;

  const [addedAttributes, setAddedAttributes] = useState<any>([]);
  const addedAttributesRef = useRef();
  addedAttributesRef.current = addedAttributes;

  const [hasAttributesBeenUnlinked, setHasAttributesBeenUnlinked] =
    useState(false);

  const [informativeAttributes, setInformativeAttributes] = useState<any>([]);
  const informativeAttributesRef = useRef();
  informativeAttributesRef.current = informativeAttributes;

  const [attributesConflictValues, setAttributesConflictValues] = useState<
    Group[]
  >([]);

  const accesToken = useSelector((state: any) => state.user.access);

  const handleEditAttribute = (editedAttribute: {
    id: number;
    name: string;
  }) => {
    setAttributesList((prevAttributes: Attribute[]) =>
      returnUpdatedAttributesList(prevAttributes, editedAttribute)
    );

    setAttributes((prevAttributes: Attribute[]) =>
      returnUpdatedAttributesList(prevAttributes, editedAttribute)
    );

    setAvailableAttributes((prevAttributes: Attribute[]) =>
      returnUpdatedAttributesList(prevAttributes, editedAttribute)
    );
  };

  const handleClickDuplicateAttribute = async (
    productId: number,
    attributeId: number
  ) => {
    const attributeToDuplicate = attributesList.find(
      (attr: Attribute) => attr.id === attributeId
    );

    const infoAttributeNames = informativeAttributes.map(
      (attr: Attribute) => attr.name
    );

    if (infoAttributeNames.includes(attributeToDuplicate.name)) {
      return;
    }

    try {
      const response = await apiAttributes.duplicateAttributeToInfo(
        accesToken,
        {
          productId,
          attributeId,
        }
      );

      const addedAttribute = {
        id: response.data.attribute.id,
        name: response.data.attribute.name,
      };

      setAvailableAttributes((attrs: Attribute[]) => [
        ...attrs,
        { ...addedAttribute, type: "info" },
      ]);
      setInformativeAttributes((attrs: Attribute[]) => [
        ...attrs,
        addedAttribute,
      ]);

      displayNotification({
        title: "Atributo duplicado correctamente",
        message: "",
        type: "success",
        dismiss: true,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const loadTableAttributes = (unprocessedAttributes: any) => {
    if (unprocessedAttributes) {
      setAttributes(
        unprocessedAttributes.map((item: any) => ({
          id: item.attribute_id,
          name: item.name,
          type: item.type,
        }))
      );
    } else {
      setAttributes([]);
    }
  };

  const loadAttributesList = (unprocessedAttributes: any) => {
    setAttributesList(
      unprocessedAttributes.map((attribute: any) => ({
        id: attribute.id,
        name: attribute.name,
      }))
    );
  };

  const loadAvailableAttributes = (unprocessedAttributes: any) => {
    setAvailableAttributes(
      unprocessedAttributes.map((item: any) => ({
        id: item.id,
        name: item.name,
        type: item.attribute_type,
      }))
    );
  };

  const loadInfoAttributes = (unprocessedAttributes: any) => {
    setInformativeAttributes(() => {
      const processedInformativeAttributes = unprocessedAttributes.map(
        (item: { id: number; name: string }) => ({
          id: item.id,
          name: item.name,
        })
      );

      return processedInformativeAttributes;
    });
  };

  const getAttributesFromDb = async (productId: number) => {
    const response = await apiProducts.filter(accesToken, {
      filtername: "id",
      value: productId,
    });

    const product = response.data[0];

    loadTableAttributes(product.table_attributes);
    loadAttributesList(product.article_attributes);
    loadAvailableAttributes(product.not_in_table_attributes);
  };

  const handleClickUnlinkAttribute = () => {
    setHasAttributesBeenUnlinked(true);
  };

  const handleClearHasAttributesBeenUnlinked = () => {
    setHasAttributesBeenUnlinked(false);
  };

  const handleAttributes409Conflict = (items: Group[]) => {
    setAttributesConflictValues(items);
  };

  const handleClean409Conflict = () => {
    setAttributesConflictValues([]);
  };

  const areAttrbuteConflictValues = attributesConflictValues.length > 0;

  return {
    availableAttributes,
    setAvailableAttributes,
    availableAttributesRef,
    attributes,
    setAttributes,
    attributesRef,
    attributesList,
    setAttributesList,
    attributesListRef,
    addedAttributes,
    setAddedAttributes,
    addedAttributesRef,
    informativeAttributes,
    setInformativeAttributes,
    informativeAttributesRef,
    handleEditAttribute,
    handleClickDuplicateAttribute,
    loadTableAttributes,
    loadAttributesList,
    loadAvailableAttributes,
    loadInfoAttributes,
    getAttributesFromDb,
    hasAttributesBeenUnlinked,
    handleClickUnlinkAttribute,
    handleClearHasAttributesBeenUnlinked,
    handleAttributes409Conflict,
    areAttrbuteConflictValues,
    attributesConflictValues,
    handleClean409Conflict,
  };
};

export default useAttributes;
